import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Container } from '@/components/layout/Container';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { zodResolver } from '@/utils/zodResolver';
import { confirmForgotPassword, describeAuthError, forgotPassword } from '../cognito';

const emailSchema = z.object({ email: z.string().trim().email('Enter a valid email address') });
const resetSchema = z.object({
  code: z.string().trim().min(4, 'Enter the code from your email'),
  password: z.string().min(10, 'Use at least 10 characters')
    .regex(/[a-z]/, 'Include a lower case letter').regex(/[A-Z]/, 'Include an upper case letter')
    .regex(/\d/, 'Include a number'),
});
type EmailValues = z.infer<typeof emailSchema>;
type ResetValues = z.infer<typeof resetSchema>;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const step1 = useForm<EmailValues>({ resolver: zodResolver<EmailValues>(emailSchema) });
  const step2 = useForm<ResetValues>({ resolver: zodResolver<ResetValues>(resetSchema) });

  return (
    <Container className="max-w-md">
      <PageHeader title="Reset your password" description={email ? `Enter the code we sent to ${email}.` : 'We will email you a reset code.'} />
      <Card><CardBody>
        {error && <div className="mb-4"><Alert tone="error">{error}</Alert></div>}
        {!email ? (
          <form noValidate className="space-y-4" aria-label="Request reset code" onSubmit={step1.handleSubmit(async (v) => {
            setError(null);
            try { await forgotPassword(v.email); setEmail(v.email); } catch (e) { setError(describeAuthError(e)); }
          })}>
            <Field label="Email" type="email" autoComplete="email" error={step1.formState.errors.email?.message} {...step1.register('email')} />
            <Button type="submit" size="lg" className="w-full" loading={step1.formState.isSubmitting}>Send code</Button>
          </form>
        ) : (
          <form noValidate className="space-y-4" aria-label="Choose a new password" onSubmit={step2.handleSubmit(async (v) => {
            setError(null);
            try { await confirmForgotPassword(email, v.code, v.password); navigate('/login', { replace: true }); }
            catch (e) { setError(describeAuthError(e)); }
          })}>
            <Field label="Code" inputMode="numeric" autoComplete="one-time-code" error={step2.formState.errors.code?.message} {...step2.register('code')} />
            <Field label="New password" type="password" autoComplete="new-password" error={step2.formState.errors.password?.message} {...step2.register('password')} />
            <Button type="submit" size="lg" className="w-full" loading={step2.formState.isSubmitting}>Set new password</Button>
          </form>
        )}
        <p className="mt-4 text-sm"><Link className="text-brand-700 hover:underline" to="/login">Back to log in</Link></p>
      </CardBody></Card>
    </Container>
  );
}
